/**
 * Changes View Component
 *
 * Displays table changes (additions, removals) between two versions.
 * Provides server-side pagination, filtering, and sorting capabilities.
 *
 * Features:
 * - Filter by change type (all, added, removed)
 * - Server-side pagination
 * - Server-side sorting
 * - Automatic column generation
 * - Empty state handling
 */

import React from 'react';
import { Plus, Minus, GitCompare, FileSpreadsheet, Loader2 } from 'lucide-react';
import { Button, Separator } from '@loader/ui';

import { DataTable } from '@/components/data-grid';
import { generateColumnsFromData } from '@/lib/tableHelpers';
import { EmptyView } from '@/components/common';
import { useDiffExport } from '@/features/tables/hooks/useDiffExport';

/**
 * Change filter type
 */
type ChangeFilter = 'all' | 'added' | 'removed';

/**
 * Props for ChangesView component
 */
interface ChangesViewProps {
  /** Table ID */
  tableId: string;
  /** Table name (for export filename) */
  tableName?: string;
  /** Version A (from) ID */
  fromVersionId: string;
  /** Version B (to) ID */
  toVersionId: string;
  /** Current diff data from hook */
  diffData: {
    data: Record<string, unknown>[];
    counts: {
      added: number;
      removed: number;
      unchanged: number;
    };
    pagination: {
      limit: number;
      offset: number;
      total: number;
      page: number;
      pages: number;
    };
  } | undefined;
  /** Loading state */
  isLoading: boolean;
  /** Error state */
  error: Error | null;
  /** Pagination handlers */
  onPaginationChange: (page: number, pageSize: number) => void;
  /** Sort handler */
  onSortChange: (sortBy: string | undefined, sortDir?: 'asc' | 'desc') => void;
  /** Filter handler */
  onFilterChange: (changeType: ChangeFilter) => void;
  /** Currently active filter */
  changeFilter: ChangeFilter;
  /** Current page index (0-based) */
  pageIndex: number;
  /** Current page size */
  pageSize: number;
  /** Current sort column */
  sortBy?: string;
  /** Current sort direction */
  sortDir?: 'asc' | 'desc';
  /** Visual variant for DataTable: 'card' (default) or 'flush' */
  variant?: 'card' | 'flush';
}

/**
 * ChangesView Component
 *
 * Renders a server-side paginated table showing table changes between versions.
 *
 * @param props - Component props
 */
export const ChangesView: React.FC<ChangesViewProps> = ({
  tableId,
  tableName,
  fromVersionId,
  toVersionId,
  diffData,
  isLoading,
  error,
  onPaginationChange,
  onSortChange,
  onFilterChange,
  changeFilter,
  pageIndex,
  pageSize,
  sortBy,
  sortDir,
  variant = 'card',
}) => {
  const { mutate: exportDiff, isPending: isExporting } = useDiffExport();
  // Extract counts for filter buttons
  const counts = diffData?.counts || {
    added: 0,
    removed: 0,
    unchanged: 0,
  };

  // Calculate total changes (excluding unchanged)
  const totalChanges = counts.added + counts.removed;

  // Format large numbers (1000+ becomes 1K, 1000000+ becomes 1M)
  const formatCount = (n: number): string => {
    if (n >= 1_000_000) return `${Math.round(n / 1_000_000)}M`;
    if (n >= 1_000) return `${Math.round(n / 1_000)}K`;
    return String(n);
  };

  // Get paginated data
  const data = diffData?.data || [];
  const pagination = diffData?.pagination || {
    limit: pageSize,
    offset: pageIndex * pageSize,
    total: 0,
    page: pageIndex + 1,
    pages: 0,
  };

  // Generate columns from data
  const columns = data.length > 0 ? generateColumnsFromData(data) : [];

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Filter buttons for diff categories */}
      <div className="mb-4 flex items-center gap-2 px-4">
        <Button
          variant={changeFilter === 'all' ? 'default' : 'outline'}
          size="sm"
          className="w-40 justify-center"
          onClick={() => onFilterChange('all')}
        >
          <GitCompare className="mr-2 h-4 w-4" />
          {`All (${formatCount(totalChanges)})`}
        </Button>
        <Button
          variant={changeFilter === 'added' ? 'default' : 'outline'}
          size="sm"
          className="w-40 justify-center"
          onClick={() => onFilterChange('added')}
        >
          <Plus className="mr-2 h-4 w-4" />
          {`Added (${formatCount(counts.added)})`}
        </Button>
        <Button
          variant={changeFilter === 'removed' ? 'default' : 'outline'}
          size="sm"
          className="w-40 justify-center"
          onClick={() => onFilterChange('removed')}
        >
          <Minus className="mr-2 h-4 w-4" />
          {`Removed (${formatCount(counts.removed)})`}
        </Button>

        <Separator orientation="vertical" className="mx-2 h-6" />

        <Button
          variant="outline"
          size="sm"
          onClick={() =>
            exportDiff({
              tableId,
              fromVersionId,
              toVersionId,
              tableName: tableName || 'table',
            })
          }
          disabled={isExporting || !fromVersionId || !toVersionId}
        >
          {isExporting ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <FileSpreadsheet className="mr-2 h-4 w-4" />
          )}
          Export to Excel
        </Button>
      </div>

      {/* Server-side DataTable */}
      <div className="min-h-0 flex-1">
        <DataTable
          data={data}
          columns={columns}
          isLoading={isLoading}
          error={error}
          // Pagination
          pageIndex={pageIndex}
          pageSize={pageSize}
          totalItems={pagination.total}
          pageCount={pagination.pages}
          onPageChange={(newPageIndex) =>
            onPaginationChange(newPageIndex, pageSize)
          }
          onPageSizeChange={(newPageSize) =>
            onPaginationChange(0, newPageSize)
          }
          // Sorting
          sortBy={sortBy}
          sortDir={sortDir}
          onSortChange={onSortChange}
          // Features
          enableFiltering={false} // We use custom filter buttons instead
          enablePagination={true}
          showFooter={true}
          hideVerticalLines={true}
          variant={variant}
          emptyStateComponent={
            <EmptyView
              title="No differences"
              description="No differences found between selected versions."
              icon={GitCompare}
            />
          }
        />
      </div>
    </div>
  );
};
